import { useEffect, useRef, useState } from "react";
import { TextInput, Button } from "@carbon/react";
import { Search, Add, Close } from "@carbon/icons-react";
import { MaterialResponse, MaterialType } from "../../types";
import { useSearchAllMaterials } from "../../hooks/useMaterials";
import useDebounce from "../../hooks/useDebounce";
import i18next from "../../i18n";
import "./materialAutocompleteCell.scss";

export type MaterialAutocompleteCellProps = {
  value?: string;
  isSelected?: boolean;
  autoFocus?: boolean;
  inputRef?: (el: HTMLInputElement | null) => void;
  onSelect: (material: MaterialResponse) => void;
  onClear: () => void;
  /** Receives whatever the user had typed, so the create form starts prefilled. */
  onCreateNew: (searchTerm: string) => void;
};

/**
 * Material search + pick + "create new" cell, rebuilt on Carbon's `TextInput`/`Button` (Task 6) in
 * place of the pre-Carbon Tailwind version — behaviour unchanged. The suggestion list stays a real
 * `<ul>`/`<li>` (each result a `listitem`, driven directly by `e2e/purchase-requests.spec.ts`), the
 * input keeps its "Search material..." accessible name and placeholder (the unit test drives it by
 * placeholder), and the create action is still pinned to the bottom whether or not the search
 * returned results — the exact regression the unit test guards against.
 */
const MaterialAutocompleteCell = ({
  value,
  isSelected,
  autoFocus,
  inputRef,
  onSelect,
  onClear,
  onCreateNew,
}: MaterialAutocompleteCellProps) => {
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [page] = useState(0);
  const { debouncedSearchTerm, isSearching } = useDebounce(searchTerm, 500);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setSearchTerm(value || "");
    }
  }, [value]);

  const { data } = useSearchAllMaterials(
    MaterialType.ALL,
    debouncedSearchTerm,
    page
  );

  const results: MaterialResponse[] = debouncedSearchTerm
    ? data?.content || []
    : [];

  const handleClear = () => {
    setSearchTerm("");
    setIsOpen(false);
    prevValueRef.current = "";
    onClear();
  };

  const handleCreateNew = () => {
    onCreateNew(searchTerm.trim());
    setIsOpen(false);
  };

  const searchLabel = i18next.t("purchaseRequests.items.searchMaterial");

  return (
    <div className="material-autocomplete">
      <div className="material-autocomplete__input">
        <Search className="material-autocomplete__search-icon" size={16} />
        <TextInput
          id="material-autocomplete-input"
          ref={inputRef}
          autoFocus={autoFocus}
          labelText={searchLabel}
          hideLabel
          placeholder={searchLabel}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (searchTerm) setIsOpen(true);
          }}
          onBlur={() => {
            setTimeout(() => setIsOpen(false), 150);
          }}
        />
        {isSelected && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={i18next.t("cta.delete")}
            className="material-autocomplete__clear"
          >
            <Close size={16} />
          </button>
        )}
      </div>

      {isOpen && searchTerm && !isSearching && (
        <div className="material-autocomplete__menu">
          {results.length > 0 && (
            <ul className="material-autocomplete__list">
              {results.map((material: MaterialResponse) => (
                <li
                  key={material.id}
                  className="material-autocomplete__option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(material);
                    setSearchTerm(material.globalMaterialName);
                    setIsOpen(false);
                  }}
                >
                  <div className="material-autocomplete__option-name">
                    {material.ean} - {material.globalMaterialName}
                  </div>
                  <div className="material-autocomplete__option-owner">
                    {material.owner?.fullName}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {results.length === 0 && (
            <p className="material-autocomplete__empty">
              {i18next.t("materials.notAvailable")}
            </p>
          )}

          <div className="material-autocomplete__create">
            <Button
              kind={results.length === 0 ? "primary" : "tertiary"}
              size="sm"
              renderIcon={Add}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleCreateNew}
            >
              {i18next.t("purchaseRequests.items.createMaterial")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialAutocompleteCell;
